import { useCallback, useEffect, useMemo } from 'react';

import { checkReviewNote } from '@psy/validations';

import { useLanguage } from '../../contexts/language';
import { useNoteSaves } from '../../contexts/note-saves';

import styles from './style.module.css';

// A clinician's private note that saves as it is written (C35): a moment
// after typing stops, when the field is left, and when the page is left with
// a change not yet saved. The saves themselves are the note saves above
// every screen (#132), so a field opened again, on this review or after
// leaving it, shows and goes on from the same note, and its writes stay in
// one order. Saving… includes the typing pause and a write on its way; Saved
// describes the current text only once a save of it has been acknowledged
// and nothing is left to send. A note with text is lilac, the colour of what
// only the clinician sees. position is null for the note on the whole test.
//
// While signing out, and after a session the page could not leave, the field
// is read-only and says so (#134): signing out saves the notes written before
// it started, and a new one could not be accounted for.
export function NoteField({ id, label, placeholder, attemptId, position, initial, rows = 2 }) {
  const { text, translate } = useLanguage();
  const { signingOut, ended, entryOf, edit, flush } = useNoteSaves();
  const entry = entryOf({ attemptId, position, initial });

  const onChange = useCallback(function({ currentTarget }) {
    edit(entry, currentTarget.value);
  }, [ entry, edit ]);

  const onBlur = useCallback(function() {
    flush(entry);
  }, [ entry, flush ]);

  // Leaving the page with a pause still running saves what was typed now
  // rather than after the pause.
  useEffect(function() {
    return function() {
      if (entry.timer != null) {
        flush(entry);
      }
    };
  }, [ entry, flush ]);

  const refused = checkReviewNote(entry.latest);
  let error = refused;

  if (error == null) {
    error = entry.failure;
  }

  const saving = entry.timer != null || entry.sending === true || entry.certain === false || entry.latest !== entry.stored;
  const locked = signingOut === true || ended === true;

  const status = useMemo(function() {
    if (locked === true) {
      return <span className={ styles.status } role='status'>{ text.noteReadOnly }</span>;
    }

    if (entry.touched === false) {
      return null;
    }

    if (error != null) {
      return <span className={ styles.error } role='alert'>{ translate(error) }</span>;
    }

    if (saving === true) {
      return <span className={ styles.status } role='status'>{ text.saving }</span>;
    }

    return <span className={ styles.status } role='status'>{ text.saved }</span>;
  }, [ locked, entry.touched, error, saving, text.noteReadOnly, text.saving, text.saved, translate ]);

  let className = styles.input;

  if (entry.latest.trim() !== '') {
    className = `${ styles.input } ${ styles.written }`;
  }

  return (
    <div className={ styles.field }>
      <div className={ styles.top }>
        <label className={ styles.label } htmlFor={ id }>{ label }</label>

        { status }
      </div>

      <textarea id={ id } className={ className } rows={ rows } placeholder={ placeholder } value={ entry.latest } readOnly={ locked } onChange={ onChange } onBlur={ onBlur }/>
    </div>
  );
}
